import React, { useEffect, useState } from 'react';
import type { RuleSet } from 'styled-components';
import { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import type { Brand } from '@/domainLogic/index.ts';
import type { TextVariant } from '@lmn/design-system';
import RoomMainAmenities, { RoomMainAmenity } from '@/app/components/hotel-detail/rooms/RoomMainAmenities';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { RoomsIncludedInPrice } from '@/app/components/hotel-detail/rooms/RoomPanelCommon';
import { EvoRoomAvailabilityAlertLabel } from '@/app/components/hotel-detail/evo/rooms/EvoRoomAvailabilityAlertLabel';
import { RoomSimpleAmenities } from './RoomSimpleAmenities';
import { RoomAvailabilityAlertLabel } from './RoomAvailabilityAlertLabel';
import customCss from '../css/customCss';
import { useTranslation } from '../../translations/Translation';
import { getMainAmenities } from './roomCommon';
import OpenXModal from '../../search/components/Modal/OpenXModal';
import ImageGalleryModalX from '../ImageGalleryModalX';
import ImageSlider from '../ImageSlider';
import { useLoadableImages } from '../hooks/useLoadableImages';
import { RoomBedOptions } from './RoomBedOptions';
import { shouldShowBedSelection } from './RoomBedTypeSelection';

function getRoomGeneralDescription(descriptionsArray: readonly DescriptionType[]): string | boolean {
  const generalDescription = descriptionsArray.find(
    (description) => description?.type === 'GENERAL' && description?.text?.length > 1
  );

  if (generalDescription) {
    return generalDescription?.text?.trim();
  }
  return false;
}

type ImageGalleryModalStateType = { isOpen: boolean | undefined; initialFocusedImageIndex: number };

type RoomMobileImageGalleryProps = {
  images: RoomType['images'];
  // Room name, used in each photo's alt text.
  name: string;
  // Slider height on mobile (px).
  height: number;
  // Apply the inset card treatment (8px radius). EVO renders the gallery full-bleed without radius.
  rounded?: boolean;
  // When set, grow the slider to this height (px) from the `sm` breakpoint and inset the overlay arrows.
  tabletTallHeight?: number;
  // Override the image-counter position within the slider (defaults to bottom/right insets).
  counterPosition?: RuleSet<object> | string;
};

// Room image slider + its full-screen gallery modal. The modal renders into a portal (and returns null
// when closed), so this can sit as the first child of a flex container without affecting the layout.
export function RoomMobileImageGallery(props: RoomMobileImageGalleryProps) {
  const { images, name, height, rounded = false, tabletTallHeight, counterPosition } = props;
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const [imagesGalleryModalState, setImagesGalleryModalState] = useState<ImageGalleryModalStateType>({
    isOpen: undefined,
    initialFocusedImageIndex: 0,
  });

  const onImageGalleryModaClose = () => {
    setImagesGalleryModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const onImageGalleryModaOpen = () => {
    setImagesGalleryModalState((prev) => ({ ...prev, isOpen: true }));
  };

  // MPH-1321: photos that fail to load leave the gallery (and its counter), here and in the fullscreen view.
  const { loadableImages, onImageFallback } = useLoadableImages(images);
  const hasLoadableImages = loadableImages.length > 0;

  // Once none of its photos is left, the fullscreen view closes the regular way, which takes it out of the
  // modal URL parameter and pops the history entry it pushed. It stays mounted for that, as unmounting it
  // open would leave both behind.
  const isFullScreenOpen = !!imagesGalleryModalState.isOpen;
  useEffect(() => {
    if (!hasLoadableImages && isFullScreenOpen) setImagesGalleryModalState((prev) => ({ ...prev, isOpen: false }));
  }, [hasLoadableImages, isFullScreenOpen]);

  if (!images.length) return null;

  return (
    <>
      {hasLoadableImages && (
        <ImageSlider
          images={loadableImages}
          imageAltName={name}
          showCounterIcon={false}
          counterPosition={counterPosition}
          onImageFallback={onImageFallback}
          onImageClickHandler={(imageIndex) =>
            setImagesGalleryModalState({ isOpen: true, initialFocusedImageIndex: imageIndex })
          }
          css={css`
            height: ${height}px;
            ${rounded ? 'border-radius: 8px;' : ''}
            ${
              tabletTallHeight
                ? css`
                    @media ${customCss.breakpoints.sm} {
                      height: ${tabletTallHeight}px;
                    }
                  `
                : ''
            }

          & .slider-container {
              ${rounded ? 'border-radius: 8px;' : ''}
              ${(p) => p.theme.kallisto.mixins.font('Caption')};
            }

            & .slider-overlay-arrow-left,
            & .slider-overlay-arrow-right {
              display: flex;

              background: rgba(255, 255, 255, 1);
              border-radius: 32px;
              width: 32px;
              height: 32px;
              align-items: center;

              &:hover {
                background: rgba(255, 255, 255, 0.9);
              }

              & > [data-library-icon] {
                color: ${(p) => p.theme.kallisto.color.icon.static.iconPrimary};
                width: 16px;
                height: 16px;
              }
            }

            /* From sm up the room gallery isn't full-bleed, so inset the arrows. The shared
             HeroGallerySliderOverlay zeroes these at sm for the full-bleed hero galleries,
             and this inset must not depend on tabletTallHeight (the EVO mobile body omits it). */
            @media ${customCss.breakpoints.sm} {
              & .slider-overlay-arrow-left {
                left: 16px;
              }

              & .slider-overlay-arrow-right {
                right: 16px;
              }
            }
          `}
        />
      )}

      <OpenXModal
        isOpen={imagesGalleryModalState.isOpen || false}
        isMobile={true}
        onClose={onImageGalleryModaClose}
        onCloseByBrowserHistory={onImageGalleryModaClose}
        onOpenByBrowserHistory={onImageGalleryModaOpen}
        fullScreen={true}
        brand={localization.brand as Brand}
        className={'room-details-gallery'}
        id="room-details-gallery"
        aria-label={t('vacation.select.details.rooms.allinfo')}
        bodyBackgroundColor="backgroundNeutralContrast"
      >
        <ImageGalleryModalX
          images={loadableImages}
          onImageFallback={onImageFallback}
          name={name}
          isOpen={imagesGalleryModalState.isOpen}
          initialFocussedImageIndex={imagesGalleryModalState.initialFocusedImageIndex}
          onDismiss={onImageGalleryModaClose}
        />
      </OpenXModal>
    </>
  );
}

type RoomMobileHeaderProps = {
  roomsIncludedInPrice: number;
  roomName: string;
  roomsLeft: number;
} & React.HTMLAttributes<HTMLElement>;

export function RoomMobileHeader(props: RoomMobileHeaderProps) {
  const { roomsIncludedInPrice, roomName, roomsLeft, ...rest } = props;
  const { localization } = usePageWrapperData();
  const evolutionEnabled = localization?.configValuesApp?.evolutionEnabled;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 4px;

        ${
          evolutionEnabled &&
          css`
            align-items: flex-start;
            gap: ${({ theme }) => theme.kallisto.space.space100};
          `
        }
      `}
      {...rest}
    >
      <div
        css={`
          display: flex;
          align-items: center;
          gap: 8px;
        `}
      >
        {roomsIncludedInPrice > 1 && <RoomsIncludedInPrice roomsIncludedInPrice={roomsIncludedInPrice} />}
        {/* MPH-1507: only the Room details dialogs render this header, so the room name is an h3 under their h2 title. */}
        <Text variant="Title4" forceTag="h3">
          {roomName}
        </Text>
      </div>
      {evolutionEnabled ? (
        <EvoRoomAvailabilityAlertLabel roomsLeft={roomsLeft} />
      ) : (
        <RoomAvailabilityAlertLabel roomsLeft={roomsLeft} />
      )}
    </div>
  );
}

type RoomMobileInfoSectionProps = {
  room: RoomType;
  selectedMealPlan?: MealPlanType;
  // Body text size for the description, amenities and bed options. Defaults to the compact `BodySmall`.
  textVariant?: TextVariant;
};

// "Room info" block: description, bed options, main + simple amenities. Renders nothing when the room
// has no descriptive content. Returns a fragment so its children flow as items of the parent container.
export function RoomMobileInfoSection(props: RoomMobileInfoSectionProps) {
  const { room, selectedMealPlan, textVariant = 'BodySmall' } = props;
  const { t } = useTranslation();

  const mainAmenities = getMainAmenities(t, room);
  const simpleAmenities = room.amenities.filter((amenity) => amenity.type === 'GENERAL');
  const description = getRoomGeneralDescription(room.descriptions);
  const hasRoomInfo = description || mainAmenities.length > 0 || simpleAmenities.length > 0;

  if (!hasRoomInfo) return null;

  return (
    <>
      <Text variant="Title4" forceTag="h3">
        {t('vacation.select.room_modal.room_description')}
      </Text>

      {room.bedOptions && !shouldShowBedSelection(selectedMealPlan?.bedId, room.bedOptions) ? (
        <RoomBedOptions bedOptions={room.bedOptions} fontSize={textVariant} />
      ) : undefined}
      {mainAmenities.length > 0 && (
        <RoomMainAmenities
          amenities={mainAmenities}
          iconColor="iconPrimary"
          iconSize={16}
          textVariant={textVariant}
          css={`
            display: flex;
            flex-direction: row;
            gap: 0 16px;
            flex-wrap: wrap;
            width: 100%;

            ${RoomMainAmenity} {
              align-items: center;
            }
          `}
        />
      )}
      {description && <Text variant={textVariant}>{description}</Text>}
      {simpleAmenities.length > 0 && (
        <RoomSimpleAmenities
          amenities={simpleAmenities}
          textVariant={textVariant}
          css={`
            flex-direction: column;
          `}
        />
      )}
    </>
  );
}
